import { useEffect, useState } from "react";
import { FileText, Mail } from "lucide-react";
import { apiClient, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = ["Draft", "Sent", "Paid", "Overdue"];
const STATUS_STYLES = {
  Draft: "bg-[#6E7E74]/25 text-[#A1B0A6]",
  Sent: "bg-blue-500/20 text-blue-300",
  Paid: "bg-emerald-500/20 text-emerald-300",
  Overdue: "bg-red-500/20 text-red-300",
};

export const InvoiceList = ({ refreshKey }) => {
  const [invoices, setInvoices] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await apiClient.get("/admin/invoices", { params });
      setInvoices(data);
    } catch {
      toast.error("Could not load invoices");
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFrom, dateTo, refreshKey]);

  const changeStatus = async (id, status) => {
    try {
      await apiClient.patch(`/admin/invoices/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      fetchInvoices();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update status");
    }
  };

  const viewPdf = async (id) => {
    try {
      const token = localStorage.getItem("ferguson_admin_token");
      const res = await fetch(`${API}/admin/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open invoice PDF");
    }
  };

  const [sendingId, setSendingId] = useState(null);
  const sendEmail = async (inv) => {
    if (!inv.bill_to_email) {
      toast.error("Add a client email address to this invoice before sending");
      return;
    }
    setSendingId(inv.id);
    try {
      await apiClient.post(`/admin/invoices/${inv.id}/send-email`);
      toast.success(`Invoice #${inv.invoice_number} emailed to ${inv.bill_to_email}`);
      fetchInvoices();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send invoice email");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div data-testid="invoice-list">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1.5">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`invoice-filter-${s.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s ? "bg-gold text-[#0A140F]" : "bg-deep-card text-[#A1B0A6] hover:text-gold"
              }`}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#A1B0A6]">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="invoice-filter-date-from" className="admin-field py-1.5" />
          <span>to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="invoice-filter-date-to" className="admin-field py-1.5" />
        </div>
      </div>

      {!invoices ? (
        <p className="text-[#A1B0A6] text-sm">Loading invoices...</p>
      ) : invoices.length === 0 ? (
        <p className="text-[#A1B0A6] text-sm" data-testid="invoice-list-empty">No invoices yet.</p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="rounded-xl border border-gold/15 bg-deep-card p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={18} className="text-gold shrink-0" />
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[#F4F7F5]">#{inv.invoice_number} — {inv.bill_to_name}</div>
                  <div className="text-xs text-[#6E7E74]">
                    {inv.date}
                    {inv.emailed_at && <span className="text-emerald-400 ml-2">· Emailed {new Date(inv.emailed_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-sm text-[#D7E0DA]">{inv.currency} {inv.total.toFixed(2)}</span>
                <select
                  value={inv.status}
                  onChange={(e) => changeStatus(inv.id, e.target.value)}
                  data-testid={`invoice-status-select-${inv.id}`}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-full border-0 ${STATUS_STYLES[inv.status]}`}
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                  onClick={() => sendEmail(inv)}
                  disabled={sendingId === inv.id || !inv.bill_to_email}
                  data-testid={`invoice-send-email-${inv.id}`}
                  title={inv.bill_to_email ? `Email to ${inv.bill_to_email}` : "Add a client email first"}
                  className="flex items-center gap-1 text-xs font-medium text-gold hover:text-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail size={13} /> {sendingId === inv.id ? "Sending..." : "Email"}
                </button>
                <button
                  onClick={() => viewPdf(inv.id)}
                  data-testid={`invoice-view-pdf-${inv.id}`}
                  className="text-xs font-medium text-gold hover:text-gold-hover"
                >
                  View PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
